import { logger } from '@heidi/runtime/logger';
import { type components, ModalViewAction } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { type UserResponse } from '../../contracts';
import { GET_USER, MARK_MODAL_AS_VIEWED_MUTATION_KEY } from '../../contracts/keys';
import { markModalAsViewed } from '../api/user';

type ModalView = components['schemas']['ModalView'];

export { MARK_MODAL_AS_VIEWED_MUTATION_KEY };

export const useMutateMarkModalAsViewed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [MARK_MODAL_AS_VIEWED_MUTATION_KEY],
    mutationFn: markModalAsViewed,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [GET_USER] });

      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);

      if (previousUser) {
        const updatedUser = produce(previousUser, (draft) => {
          const now = new Date().toISOString();
          if (!draft.seen_modals) {
            draft.seen_modals = {};
          }
          const seenModals = draft.seen_modals as Record<string, ModalView>;
          const existing = seenModals[variables.modal_id] as ModalView | undefined;
          // BE pass-through: omitted or null dismissal controls leave existing values unchanged.
          const nextHideForever =
            variables.hide_forever == null
              ? (existing?.hide_forever ?? false)
              : variables.hide_forever;
          const nextSnoozedUntil =
            variables.snoozed_until == null
              ? existing?.snoozed_until
              : variables.snoozed_until;
          if (variables.action === ModalViewAction.dismiss) {
            // Do not bump `view_count` on dismiss — mount / legacy increment / engage
            // already record impressions; counting dismiss too double-counted one
            // consult for `view_count - engage_count` rules (e.g. export banner).
            seenModals[variables.modal_id] = {
              ...existing,
              modal_id: variables.modal_id,
              first_seen_at: existing?.first_seen_at ?? now,
              last_seen_at: now,
              view_count: existing?.view_count ?? 0,
              consecutive_dismiss_count: (existing?.consecutive_dismiss_count ?? 0) + 1,
              engage_count: existing?.engage_count ?? 0,
              last_engaged_at: existing?.last_engaged_at ?? null,
              hide_forever: nextHideForever,
              snoozed_until: nextSnoozedUntil,
            };
          } else if (variables.action === ModalViewAction.engage) {
            seenModals[variables.modal_id] = {
              ...existing,
              modal_id: variables.modal_id,
              first_seen_at: existing?.first_seen_at ?? now,
              last_seen_at: now,
              view_count: (existing?.view_count ?? 0) + 1,
              consecutive_dismiss_count: 0,
              engage_count: (existing?.engage_count ?? 0) + 1,
              last_engaged_at: now,
              hide_forever: nextHideForever,
              snoozed_until: nextSnoozedUntil,
            };
          } else {
            // Legacy behavior. Mirrors the BE contract: increment_count=false
            // only records the first view (no-op for repeat views) so callers
            // like dismiss() don't inflate view_count past what the BE stores.
            const isFirstView = !existing;
            const shouldIncrement = variables.increment_count !== false || isFirstView;
            seenModals[variables.modal_id] = {
              ...existing,
              modal_id: variables.modal_id,
              first_seen_at: existing?.first_seen_at ?? now,
              last_seen_at: now,
              view_count: shouldIncrement
                ? (existing?.view_count ?? 0) + 1
                : (existing?.view_count ?? 0),
              consecutive_dismiss_count: existing?.consecutive_dismiss_count ?? 0,
              engage_count: existing?.engage_count ?? 0,
              last_engaged_at: existing?.last_engaged_at ?? null,
              hide_forever: nextHideForever,
              snoozed_until: nextSnoozedUntil,
            };
          }
        });

        queryClient.setQueryData<UserResponse>([GET_USER], updatedUser);
      }

      return { previousUser };
    },
    onSuccess: async (response) => {
      const previousUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      if (previousUser && response.data.seen_modals) {
        const updatedUser = produce(previousUser, (draft) => {
          draft.seen_modals = response.data.seen_modals;
        });

        queryClient.setQueryData<UserResponse>([GET_USER], updatedUser);
      }

      await queryClient.invalidateQueries({ queryKey: [GET_USER] });
    },
    onError: (error, variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData([GET_USER], context.previousUser);
        logger.error('[Modal Queue] Rolled back optimistic update due to error', error);
      } else {
        logger.error('Failed to mark modal as viewed', error);
      }
    },
  });
};
